import moment from 'moment';

/**
 * Maps layer-library group ids to their development-site `record_type`.
 *
 * The "Latest update" date shown for these groups is a hardcoded string baked
 * into the external layer-library definition (dingo-maps `library.json`), which
 * goes stale because the pins/data are now sourced from OLAP `development_sites`.
 * We override the static date with the live max(last_updated_date) per record_type.
 *
 * Group ids are stable and identical across dev and production library.json.
 */
export const DEVELOPMENT_SITES_GROUP_RECORD_TYPES = {
    housing_starts: 'housing',
    influencer_data: 'commercial',
};

/**
 * Formats an OLAP `last_updated_date` (text, `YYYY-MM-DD`) into the
 * "Month, YYYY" style used by the layer-library `latestUpdate` field
 * (e.g. "2026-06-03" -> "June, 2026"). Returns null for invalid/empty input.
 *
 * @param {string} date
 * @returns {string|null}
 */
export function formatLatestUpdate(date) {
    if (!date) return null;
    const parsed = moment.utc(date, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.format('MMMM, YYYY') : null;
}

/**
 * Overrides `metadata.latestUpdate` for the development-site layer groups with
 * the live date sourced from OLAP. Mutates the matching group's metadata in
 * place and also returns the groups for convenience. Groups without a mapping,
 * or record_types missing from `datesByRecordType`, are left untouched.
 *
 * @param {Array<{id: string, metadata: Object}>} groups
 * @param {Object.<string, {last_updated_date: string}>} datesByRecordType
 *        e.g. { housing: { last_updated_date: '2026-06-03' }, commercial: {...} }
 * @returns {Array} the same groups array
 */
export function applyOlapLatestUpdate(groups, datesByRecordType) {
    if (!Array.isArray(groups) || !datesByRecordType) return groups;

    groups.forEach(group => {
        const recordType = DEVELOPMENT_SITES_GROUP_RECORD_TYPES[group && group.id];
        if (!recordType) return;

        const entry = datesByRecordType[recordType];
        const formatted = entry && formatLatestUpdate(entry.last_updated_date);
        if (formatted && group.metadata) {
            group.metadata.latestUpdate = formatted;
        }
    });

    return groups;
}
